import { db, storage } from '../firebase/config';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  setDoc 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';

// Collection reference
const portfolioCollectionRef = collection(db, 'portfolioItems');

/**
 * Get all portfolio items for a specific user
 * @param {string} userId - The user ID
 * @returns {Promise<Array>} - Array of portfolio items
 */
export const getPortfolioItems = async (userId) => {
  try {
    const q = query(
      portfolioCollectionRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting portfolio items:', error);
    throw error;
  }
};

/**
 * Add a new portfolio item
 * @param {Object} portfolioData - The portfolio item data
 * @param {File} coverImageFile - The cover image file
 * @param {Array<File>} additionalImageFiles - Additional image files
 * @returns {Promise<string>} - The ID of the new portfolio item
 */
export const addPortfolioItem = async (portfolioData, coverImageFile, additionalImageFiles = []) => {
  try {
    // Upload cover image
    let coverImageUrl = '';
    if (coverImageFile) {
      const coverImageRef = ref(storage, `portfolio/${portfolioData.userId}/${Date.now()}_cover`);
      await uploadBytes(coverImageRef, coverImageFile);
      coverImageUrl = await getDownloadURL(coverImageRef);
    }
    
    // Upload additional images
    const additionalImageUrls = [];
    for (const file of additionalImageFiles) {
      const imageRef = ref(storage, `portfolio/${portfolioData.userId}/${Date.now()}_${file.name}`);
      await uploadBytes(imageRef, file);
      const imageUrl = await getDownloadURL(imageRef);
      additionalImageUrls.push(imageUrl);
    }
    
    // Use the provided ID if available, otherwise let Firestore generate one
    if (portfolioData.id && portfolioData.id.startsWith('portfolio-')) {
      // Use setDoc with the provided ID
      const portfolioRef = doc(db, 'portfolioItems', portfolioData.id);
      await setDoc(portfolioRef, {
        ...portfolioData,
        coverImage: coverImageUrl,
        images: additionalImageUrls,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      return portfolioData.id;
    } else {
      // Add document to Firestore with auto-generated ID
      const docRef = await addDoc(portfolioCollectionRef, {
        ...portfolioData,
        coverImage: coverImageUrl,
        images: additionalImageUrls,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      return docRef.id;
    }
  } catch (error) {
    console.error('Error adding portfolio item:', error);
    throw error;
  }
};

/**
 * Update an existing portfolio item
 * @param {string} itemId - The portfolio item ID
 * @param {Object} portfolioData - The updated portfolio data
 * @param {File} coverImageFile - The new cover image file (if any)
 * @param {Array<File>} additionalImageFiles - New additional image files (if any)
 * @returns {Promise<void>}
 */
export const updatePortfolioItem = async (itemId, portfolioData, coverImageFile, additionalImageFiles = []) => {
  try {
    const portfolioRef = doc(db, 'portfolioItems', itemId);
    const updateData = { ...portfolioData, updatedAt: new Date() };
    
    // Upload new cover image if provided
    if (coverImageFile) {
      const coverImageRef = ref(storage, `portfolio/${portfolioData.userId}/${Date.now()}_cover`);
      await uploadBytes(coverImageRef, coverImageFile);
      updateData.coverImage = await getDownloadURL(coverImageRef);
    }
    
    // Upload new additional images if provided
    if (additionalImageFiles.length > 0) {
      const newImageUrls = [];
      
      // Keep existing images that weren't removed
      if (portfolioData.images) {
        newImageUrls.push(...portfolioData.images);
      }
      
      // Add new images
      for (const file of additionalImageFiles) {
        const imageRef = ref(storage, `portfolio/${portfolioData.userId}/${Date.now()}_${file.name}`);
        await uploadBytes(imageRef, file);
        const imageUrl = await getDownloadURL(imageRef);
        newImageUrls.push(imageUrl);
      }
      
      updateData.images = newImageUrls;
    }
    
    // Check if document exists first
    try {
      // Try to update the document
      await updateDoc(portfolioRef, updateData);
    } catch (updateError) {
      // If document doesn't exist, create it with setDoc
      if (updateError.code === 'not-found' || updateError.message.includes('No document to update')) {

        // Make sure we have all required fields for a new document
        if (!updateData.createdAt) {
          updateData.createdAt = new Date();
        }
        await setDoc(portfolioRef, updateData);
      } else {
        // If it's another error, rethrow it
        throw updateError;
      }
    }
  } catch (error) {
    console.error('Error updating portfolio item:', error);
    throw error;
  }
};

/**
 * Delete a portfolio item
 * @param {string} itemId - The portfolio item ID
 * @param {string} userId - The user ID
 * @param {string} coverImageUrl - The cover image URL to delete
 * @param {Array<string>} imageUrls - Additional image URLs to delete
 * @returns {Promise<void>}
 */
export const deletePortfolioItem = async (itemId, userId, coverImageUrl, imageUrls = []) => {
  try {
    // Delete the document from Firestore first
    const portfolioRef = doc(db, 'portfolioItems', itemId);
    await deleteDoc(portfolioRef);
    
    // Helper function to safely delete a file from storage
    const safelyDeleteFile = async (url) => {
      if (!url) return;
      
      try {
        // Check if the URL is a full URL or a storage path
        let storagePath;
        if (url.startsWith('https://firebasestorage.googleapis.com')) {
          // Extract the path from the URL
          // This is a simplified approach - may need adjustment based on your URL format
          const urlObj = new URL(url);
          const fullPath = urlObj.pathname.split('/o/')[1];
          if (fullPath) {
            storagePath = decodeURIComponent(fullPath);
          } else {

            return;
          }
        } else {
          // Assume it's already a storage path
          storagePath = url;
        }
        
        const fileRef = ref(storage, storagePath);
        await deleteObject(fileRef);

      } catch (error) {
        // Check if it's a 'not-found' error
        if (error.code === 'storage/object-not-found') {

        } else {
          console.error('Error deleting file:', error);
        }
        // Continue with deletion even if file deletion fails
      }
    };
    
    // Delete cover image from Storage
    if (coverImageUrl) {
      await safelyDeleteFile(coverImageUrl);
    }
    
    // Delete additional images from Storage
    for (const imageUrl of imageUrls) {
      await safelyDeleteFile(imageUrl);
    }
  } catch (error) {
    console.error('Error deleting portfolio item:', error);
    throw error;
  }
};
